import React, { useEffect, useState, useCallback } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Users from 'lucide-react/dist/esm/icons/users';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Search from 'lucide-react/dist/esm/icons/search';
import Square from 'lucide-react/dist/esm/icons/square';
import CalendarDays from 'lucide-react/dist/esm/icons/calendar-days';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Upload from 'lucide-react/dist/esm/icons/upload';
import { useSurveyStore } from '../../stores/surveyStore';
import { useUIStore } from '../../stores/uiStore';
import { useOrgStore } from '../../stores/orgStore';
import { getTranslation } from '../../i18n';
import type { SurveyStatus, SurveySummary } from './types';
import { formatMessageTime } from '../../utils/markdown';
import { Button, Badge, ConfirmationDialog } from '../../components/ui';
import SurveyUploadModal from './SurveyUploadModal';

const SurveyBoard: React.FC = () => {
  const { surveys, isLoading, error, loadSurveys, loadSurvey, setView, setActiveSurvey, stopSurvey, deleteSurvey } = useSurveyStore();
  const { language } = useUIStore();
  const currentUserId = useOrgStore((s) => s.userContext?.id);
  const t = useCallback((key: string, vars?: Record<string, string | number>) => getTranslation(language, key, vars ?? {}), [language]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SurveySummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [uploadTarget, setUploadTarget] = useState<SurveySummary | null>(null);

  const STATUS_FILTERS: { key: string; label: string }[] = [
    { key: 'all', label: t('surveyBoardFilterAll') },
    { key: 'draft', label: t('surveyBoardFilterDrafts') },
    { key: 'in_progress', label: t('surveyBoardFilterInProgress') },
    { key: 'completed', label: t('surveyBoardFilterCompleted') },
  ];

  const STATUS_BADGE: Record<SurveyStatus, { variant: 'neutral' | 'info' | 'success' | 'warning' | 'danger'; label: string }> = {
    draft: { variant: 'neutral', label: t('surveyBoardStatusDraft') },
    in_progress: { variant: 'info', label: t('surveyBoardStatusInProgress') },
    completed: { variant: 'success', label: t('surveyBoardStatusCompleted') },
  };

  useEffect(() => {
    loadSurveys(activeFilter === 'all' ? undefined : activeFilter);
  }, [loadSurveys, activeFilter]);

  const handleCardClick = useCallback((survey: SurveySummary) => {
    loadSurvey(survey.id).then(() => {
      if (survey.status === 'completed') {
        setView('results');
      } else {
        setView('editor');
      }
    });
  }, [loadSurvey, setView]);

  const handleNewSurvey = useCallback(() => {
    setActiveSurvey(null);
    setView('editor');
  }, [setActiveSurvey, setView]);

  const handleStop = useCallback(async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setStoppingId(id);
    try {
      await stopSurvey(id);
      loadSurveys(activeFilter === 'all' ? undefined : activeFilter);
    } finally {
      setStoppingId(null);
    }
  }, [stopSurvey, loadSurveys, activeFilter]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, survey: SurveySummary) => {
    e.stopPropagation();
    setDeleteTarget(survey);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSurvey(deleteTarget.id);
      loadSurveys(activeFilter === 'all' ? undefined : activeFilter);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteSurvey, loadSurveys, activeFilter]);

  const handleUploadClick = useCallback((e: React.MouseEvent, survey: SurveySummary) => {
    e.stopPropagation();
    setUploadTarget(survey);
  }, []);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-subtle">
        <div className="flex items-center gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                activeFilter === f.key
                  ? 'bg-brand/15 text-primary'
                  : 'text-content-muted hover:text-content-secondary hover:bg-surface-primary'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <Button variant="primary" size="sm" leftIcon={<Plus size={16} />} onClick={handleNewSurvey}>
          {t('surveyBoardBtnNewSurvey')}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading && surveys.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-40 rounded-xl bg-surface-primary animate-pulse" />
            ))}
          </div>
        ) : error && surveys.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-content-muted text-sm">
            <p>{t('surveyBoardFailedToLoad')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => loadSurveys(activeFilter === 'all' ? undefined : activeFilter)}
            >
              {t('surveyBoardBtnRetry')}
            </Button>
          </div>
        ) : surveys.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center h-64 text-content-muted">
            <Search size={40} className="mb-4 opacity-30" />
            <p className="text-sm">{t('surveyBoardNoneYet')}</p>
            <Button variant="primary" size="sm" leftIcon={<Plus size={16} />} className="mt-4" onClick={handleNewSurvey}>
              {t('surveyBoardBtnCreateFirst')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {surveys.map((survey) => {
              const badge = STATUS_BADGE[survey.status] || STATUS_BADGE.draft;
              const isCreator = currentUserId != null && String(survey.created_by) === String(currentUserId);
              return (
                <div
                  key={survey.id}
                  onClick={() => handleCardClick(survey)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(survey); } }}
                  className="relative flex flex-col gap-3 p-4 rounded-xl bg-surface-primary border border-subtle hover:bg-surface-secondary hover:border-default transition-all duration-200 text-left group cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-medium text-content line-clamp-2 flex-1">
                      {survey.title || t('surveyBoardCardUntitled')}
                    </h3>
                    <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                  </div>

                  <div className="flex items-center gap-4 text-[11px] text-content-muted">
                    <span className="flex items-center gap-1">
                      <MessageSquare size={12} />
                      {survey.question_count} Q
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={12} />
                      {survey.response_count}/{survey.recipient_count}
                    </span>
                    {survey.deadline && (
                      <span className="flex items-center gap-1">
                        <CalendarDays size={12} />
                        {new Date(survey.deadline).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 mt-auto pt-2 border-t border-subtle">
                    <span className="text-[11px] text-content-muted truncate max-w-[120px]">
                      {survey.creator_name || t('surveyBoardCardUnknownCreator')}
                    </span>
                    <div className="flex items-center gap-3 ml-auto text-[11px] text-content-muted">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {formatMessageTime(survey.updated_at)}
                      </span>
                      {survey.status === 'in_progress' && (
                        <Button
                          variant="danger"
                          size="sm"
                          leftIcon={<Square size={12} />}
                          onClick={(e) => handleStop(e as React.MouseEvent, survey.id)}
                          loading={stoppingId === survey.id}
                          disabled={stoppingId !== null}
                        >
                          {t('surveyBoardBtnStop')}
                        </Button>
                      )}
                      {survey.question_count > 0 && survey.status !== 'draft' && (
                        <button
                          type="button"
                          onClick={(e) => handleUploadClick(e, survey)}
                          title={t('surveyBoardUploadTooltip')}
                          className="flex items-center justify-center text-content-muted hover:text-brand transition-colors duration-150"
                        >
                          <Upload size={12} />
                        </button>
                      )}
                      {isCreator && (
                        <button
                          type="button"
                          onClick={(e) => handleDeleteClick(e, survey)}
                          title={t('surveyBoardDeleteTooltip')}
                          className="flex items-center justify-center text-content-muted hover:text-status-error transition-colors duration-150"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title={t('surveyBoardDeleteTitle')}
        message={t('surveyBoardDeleteMessage', {
          title: deleteTarget?.title || t('surveyBoardCardUntitled'),
        })}
        confirmText={deleting ? t('surveyBoardDeleteDeleting') : t('surveyBoardDeleteConfirm')}
        cancelText={t('surveyBoardBtnCancel')}
        variant="danger"
      />

      {uploadTarget && (
        <SurveyUploadModal
          isOpen
          onClose={() => setUploadTarget(null)}
          survey={uploadTarget}
          onSuccess={() => loadSurveys(activeFilter === 'all' ? undefined : activeFilter)}
        />
      )}
    </div>
  );
};

export default React.memo(SurveyBoard);
